import { computed, inject } from '@angular/core';

import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  type,
  withComputed,
  withMethods,
  withState
} from '@ngrx/signals';
import {
  addEntities,
  entityConfig,
  removeAllEntities,
  setAllEntities,
  withEntities
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { mergeMap, of, pipe } from 'rxjs';

import { StrictEntityMap } from 'src/app/shared/models/entity.models';
import { RxMethodCallback } from 'src/app/shared/models/rxMethod.models';
import { SelectedEventDetailsStore } from 'src/app/shared/services/events/events.state';
import { defaultSelectedEventRequestsState } from 'src/app/shared/services/events/requests/event-requests.constants';
import {
  EventRequest,
  ExportEventRequestsRequest,
  GetEventRequestsPayload,
  PaginatedEventRequestsResponse
} from 'src/app/shared/services/events/requests/event-requests.models';
import { EventRequestsService } from 'src/app/shared/services/events/requests/event-requests.service';
import { ExportResponse } from 'src/app/shared/services/utils/shared-utils.models';

export interface SelectedEventRequestsState {
  fetchRequestsInflight: boolean;
  exportInflight: boolean;
}

const requestsCollection = entityConfig({
  entity: type<EventRequest>(),
  selectId: (request) => request.requestId,
  collection: 'requests'
});

export const SelectedEventRequestsStore = signalStore(
  { providedIn: 'root' },
  withState<SelectedEventRequestsState>(defaultSelectedEventRequestsState),
  withEntities(requestsCollection),
  withComputed((store) => ({
    strictRequestsEntityMap: computed(
      () => store.requestsEntityMap() as StrictEntityMap<EventRequest>
    )
  })),
  withMethods((store) => {
    const eventRequestsService = inject(EventRequestsService);
    const eventStore = inject(SelectedEventDetailsStore);
    return {
      fetchRequests: rxMethod<
        GetEventRequestsPayload &
          RxMethodCallback<PaginatedEventRequestsResponse>
      >(
        pipe(
          mergeMap(
            ({ size, page, payload, replaceState, onSuccess, onFail }) => {
              const eventId = eventStore.eventId();
              if (!eventId) {
                onFail?.();
                return of(null);
              }

              patchState<SelectedEventRequestsState>(store, {
                fetchRequestsInflight: true
              });

              return eventRequestsService
                .getEventRequests$(eventId, size, page, payload)
                .pipe(
                  tapResponse({
                    next: (requestResponse) => {
                      patchState(
                        store,
                        replaceState
                          ? setAllEntities(
                              requestResponse.content,
                              requestsCollection
                            )
                          : addEntities(
                              requestResponse.content,
                              requestsCollection
                            )
                      );
                      patchState<SelectedEventRequestsState>(store, {
                        fetchRequestsInflight: false
                      });

                      onSuccess?.(requestResponse);
                    },
                    error: () => {
                      patchState<SelectedEventRequestsState>(store, {
                        fetchRequestsInflight: false
                      });

                      onFail?.();
                    }
                  })
                );
            }
          )
        )
      ),
      export: rxMethod<
        ExportEventRequestsRequest & RxMethodCallback<ExportResponse>
      >(
        pipe(
          mergeMap(({ payload, onSuccess, onFail }) => {
            const eventId = eventStore.eventId();
            if (!eventId) {
              onFail?.();
              return of(null);
            }

            patchState<SelectedEventRequestsState>(store, {
              exportInflight: true
            });

            return eventRequestsService.exportRequests$(eventId, payload).pipe(
              tapResponse({
                next: (exportContactsResponse) => {
                  patchState<SelectedEventRequestsState>(store, {
                    exportInflight: false
                  });
                  onSuccess?.(exportContactsResponse);
                },
                error: () => {
                  patchState<SelectedEventRequestsState>(store, {
                    exportInflight: false
                  });
                  onFail?.();
                }
              })
            );
          })
        )
      ),
      reset: () => {
        patchState(store, defaultSelectedEventRequestsState);
        patchState(store, removeAllEntities(requestsCollection));
      }
    };
  })
);
